import React from 'react';
import { Table, Tag, Card, Statistic, Row, Col, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { VendorSummary, ReportSummary, ReconciliationItem } from '../../../services/pwd310Api';

interface ResultTableProps {
  data: VendorSummary[];
  summary: ReportSummary | null;
  loading: boolean;
  queried: boolean;
  noData: boolean;
}

const DOC_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  receipt: { label: '進貨', color: 'blue' },
  return: { label: '退貨', color: 'orange' },
  payment: { label: '付款', color: 'green' },
};

const fmt = (v: number) => v.toLocaleString('zh-TW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ResultTable: React.FC<ResultTableProps> = ({ data, summary, loading, queried, noData }) => {
  // Detail columns for expandable rows
  const itemColumns: ColumnsType<ReconciliationItem> = [
    {
      title: '單據類型',
      dataIndex: 'doc_type',
      key: 'doc_type',
      width: 90,
      render: (val: string) => {
        const cfg = DOC_TYPE_LABELS[val] || { label: val, color: 'default' };
        return <Tag color={cfg.color}>{cfg.label}</Tag>;
      },
    },
    { title: '單號', dataIndex: 'doc_no', key: 'doc_no', width: 140 },
    { title: '日期', dataIndex: 'doc_date', key: 'doc_date', width: 110 },
    {
      title: '金額',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (v: number) => fmt(v),
    },
  ];

  // Main columns
  const columns: ColumnsType<VendorSummary> = [
    { title: '廠商代號', dataIndex: 'vendor_code', key: 'vendor_code', width: 100 },
    { title: '廠商名稱', dataIndex: 'vendor_name', key: 'vendor_name', width: 160 },
    { title: '幣別', dataIndex: 'currency', key: 'currency', width: 70 },
    {
      title: '進貨金額',
      dataIndex: 'receipt_total',
      key: 'receipt_total',
      width: 130,
      align: 'right' as const,
      render: (v: number) => <span style={{ color: '#1890ff' }}>{fmt(v)}</span>,
    },
    {
      title: '退貨金額',
      dataIndex: 'return_total',
      key: 'return_total',
      width: 130,
      align: 'right' as const,
      render: (v: number) => <span style={{ color: '#fa8c16' }}>{fmt(v)}</span>,
    },
    {
      title: '付款金額',
      dataIndex: 'payment_total',
      key: 'payment_total',
      width: 130,
      align: 'right' as const,
      render: (v: number) => <span style={{ color: '#52c41a' }}>{fmt(v)}</span>,
    },
    {
      title: '餘額',
      dataIndex: 'balance',
      key: 'balance',
      width: 130,
      align: 'right' as const,
      render: (v: number) => (
        <span style={{ fontWeight: 600, color: v >= 0 ? '#cf1322' : '#389e0d' }}>{fmt(v)}</span>
      ),
    },
    {
      title: '明細筆數',
      key: 'item_count',
      width: 80,
      align: 'center' as const,
      render: (_: unknown, record: VendorSummary) => record.items?.length || 0,
    },
  ];

  if (!queried) return null;
  if (noData) return <Empty description="查無符合條件的對帳資料" style={{ marginTop: 40 }} />;

  return (
    <>
      {summary && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={24}>
            <Col span={5}>
              <Statistic title="廠商數" value={summary.vendor_count} />
            </Col>
            <Col span={5}>
              <Statistic title="進貨合計" value={fmt(summary.total_receipt)} valueStyle={{ color: '#1890ff' }} />
            </Col>
            <Col span={5}>
              <Statistic title="退貨合計" value={fmt(summary.total_return)} valueStyle={{ color: '#fa8c16' }} />
            </Col>
            <Col span={5}>
              <Statistic title="付款合計" value={fmt(summary.total_payment)} valueStyle={{ color: '#52c41a' }} />
            </Col>
            <Col span={4}>
              <Statistic
                title="餘額合計"
                value={fmt(summary.total_balance)}
                valueStyle={{ color: summary.total_balance >= 0 ? '#cf1322' : '#389e0d', fontWeight: 600 }}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Table<VendorSummary>
        columns={columns}
        dataSource={data}
        rowKey={(r) => `${r.vendor_code}_${r.currency}`}
        loading={loading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true, showTotal: (t) => `共 ${t} 筆` }}
        scroll={{ x: 900 }}
        expandable={{
          expandedRowRender: (record) => (
            <Table<ReconciliationItem>
              columns={itemColumns}
              dataSource={record.items}
              rowKey={(item) => `${item.doc_type}_${item.doc_no}_${item.amount}`}
              size="small"
              pagination={false}
              style={{ margin: 0 }}
            />
          ),
          rowExpandable: (record) => record.items && record.items.length > 0,
        }}
      />
    </>
  );
};

export default ResultTable;
